import {
	PropsWithChildren,
	ReactNode,
	createContext,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useResizeDetector } from 'react-resize-detector';

export type PopoverBounds = { x: number; y: number; width: number; height: number };
type Direction = 'north' | 'south';
type Children = (direction: Direction) => ReactNode;

const Context = createContext(
	undefined as unknown as {
		updatePopover: (id: string, bounds: PopoverBounds, children: Children) => void;
		hidePopover: (id: string) => void;
	},
);

export function anchorBoundsFromRect(
	rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
): PopoverBounds {
	return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
}

export function popoverOpensBelow(bounds: PopoverBounds, popoverHeight: number | undefined) {
	return bounds.y - (popoverHeight ?? 0) < 0;
}

export function fixedPopoverStyle(bounds: PopoverBounds, popoverHeight: number | undefined) {
	const south = popoverOpensBelow(bounds, popoverHeight);

	return {
		position: 'fixed' as const,
		left: bounds.x + bounds.width / 2,
		top: bounds.y + (south ? bounds.height : 0),
		transform: south ? 'translateX(-50%)' : 'translateX(-50%) translateY(-100%)',
		zIndex: 6,
	};
}

function sameBounds(a: PopoverBounds, b: PopoverBounds) {
	return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

function PopoverContainer(props: { bounds: PopoverBounds; children: Children }) {
	const { height, ref } = useResizeDetector();
	const south = popoverOpensBelow(props.bounds, height);

	return (
		<div ref={ref} style={fixedPopoverStyle(props.bounds, height)}>
			{props.children(south ? 'south' : 'north')}
		</div>
	);
}

export default function PopoverProvider(props: PropsWithChildren<{}>) {
	const [popovers, setPopovers] = useState(
		new Map<
			string,
			{
				bounds: PopoverBounds;
				children: Children;
			}
		>(),
	);

	const updatePopover = useRef(function updatePopover(
		id: string,
		bounds: PopoverBounds,
		children: Children,
	) {
		setPopovers((popovers) => {
			const current = popovers.get(id);
			if (current && sameBounds(current.bounds, bounds) && current.children === children) {
				return popovers;
			}

			popovers = new Map(popovers);
			popovers.set(id, { bounds, children });
			return popovers;
		});
	}).current;

	const hidePopover = useRef(function hidePopover(id: string) {
		setPopovers((popovers) => {
			popovers = new Map(popovers);
			popovers.delete(id);
			return popovers;
		});
	}).current;

	const value = useRef({ updatePopover, hidePopover }).current;

	return (
		<Context.Provider value={value}>
			{props.children}
			{typeof document !== 'undefined' &&
				popovers.size > 0 &&
				createPortal(
					[...popovers.entries()].map(([id, { bounds, children }]) => (
						<PopoverContainer key={id} bounds={bounds}>
							{children}
						</PopoverContainer>
					)),
					document.body,
				)}
		</Context.Provider>
	);
}

export function PopoverPortal(props: { children: (direction: Direction) => ReactNode }) {
	const context = useContext(Context);
	const id = useRef(Math.random().toString()).current;
	const ref = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		const measure = () => {
			context?.updatePopover(
				id,
				anchorBoundsFromRect(element.getBoundingClientRect()),
				props.children,
			);
		};

		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(element);
		window.addEventListener('scroll', measure, { capture: true, passive: true });
		window.addEventListener('resize', measure, { passive: true });

		return () => {
			observer.disconnect();
			window.removeEventListener('scroll', measure, true);
			window.removeEventListener('resize', measure);
		};
	}, [context, id, props.children]);

	useLayoutEffect(() => {
		return () => context?.hidePopover(id);
	}, [context, id]);

	return (
		<div
			ref={ref}
			style={{
				position: 'absolute',
				width: '100%',
				height: '100%',
				pointerEvents: 'none',
			}}
		/>
	);
}
